import { useState } from 'react';
import Modal from 'antd/lib/modal';
import Button from 'antd/lib/button';
import Table from 'antd/lib/table';
import type { ColumnsType } from 'antd/lib/table';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Select from 'antd/lib/select';
import Alert from 'antd/lib/alert';
import Tag from 'antd/lib/tag';
import Space from 'antd/lib/space';
import Typography from 'antd/lib/typography';
import Popconfirm from 'antd/lib/popconfirm';
import Divider from 'antd/lib/divider';
import { useApiKeysQuery, useCreateApiKeyMutation, useRevokeApiKeyMutation } from '../../domain/api-key/queries';
import { API_KEY_SCOPES, IApiKey, IApiKeyCreated, ICreateApiKey } from '../../domain/api-key/interface';
import { getComputerFormattedTime } from '../../domain/date/date';
import Card from 'antd/es/card/Card';
import Meta from 'antd/lib/card/Meta';
import { BookOpenText, KeyRound, SquareArrowOutUpRight } from 'lucide-react';

interface IApiKeysModalProps {
    onClose: () => void;
}

export const ApiKeysModal = ({ onClose }: IApiKeysModalProps) => {
    const [form] = Form.useForm();
    const [showForm, setShowForm] = useState(false);
    const [createdKey, setCreatedKey] = useState<IApiKeyCreated | null>(null);

    const { data, isLoading } = useApiKeysQuery();
    const createMutation = useCreateApiKeyMutation();
    const revokeMutation = useRevokeApiKeyMutation();

    const apiKeys = data?.data ?? [];

    const openForm = () => {
        setCreatedKey(null);
        setShowForm(true);
    };

    const cancelForm = () => {
        form.resetFields();
        setShowForm(false);
    };

    const onCreate = async (values: ICreateApiKey) => {
        const created = await createMutation.mutateAsync({
            description: values.description?.trim() || undefined,
            scope: values.scope,
        });
        setCreatedKey(created);
        form.resetFields();
        setShowForm(false);
    };

    const columns: ColumnsType<IApiKey> = [
        {
            title: 'API Key',
            key: 'key',
            render: (_, record) => (
                <Space direction="vertical" size={4} style={{ display: 'flex' }}>
                    <Typography.Text code style={{ fontSize: 13 }}>
                        {record.title}
                    </Typography.Text>
                    {record.description && (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            {record.description}
                        </Typography.Text>
                    )}
                    {record.scope?.length > 0 && (
                        <span>
                            {record.scope.map(s => (
                                <Tag key={s} color="blue" style={{ marginInlineEnd: 4 }}>
                                    {s}
                                </Tag>
                            ))}
                        </span>
                    )}
                </Space>
            ),
        },
        {
            title: 'Last used',
            dataIndex: 'date_last_used',
            key: 'date_last_used',
            render: (date: string | null) => getComputerFormattedTime(date),
        },
        {
            title: 'Created',
            dataIndex: 'date_created',
            key: 'date_created',
            render: (date: string) => getComputerFormattedTime(date),
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => (record.date_revoked ? <Tag color="red">Revoked</Tag> : <Tag color="green">Active</Tag>),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) =>
                record.date_revoked ? null : (
                    <Popconfirm
                        title="Revoke this API key?"
                        description="Applications using this key will stop working. This cannot be undone."
                        okText="Revoke"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => revokeMutation.mutate(record.id)}
                    >
                        <Button type="primary" danger size="small" loading={revokeMutation.isLoading && revokeMutation.variables === record.id}>
                            Revoke
                        </Button>
                    </Popconfirm>
                ),
        },
    ];

    return (
        <Modal
            open
            destroyOnHidden
            keyboard={false}
            closable={false}
            maskClosable={false}
            width={960}
            title="API Keys"
            onCancel={onClose}
            footer={[
                <Button key="close" onClick={onClose}>
                    Close
                </Button>,
            ]}
        >
            {createdKey && (
                <Alert
                    type="success"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="API key created"
                    closable
                    onClose={() => setCreatedKey(null)}
                    description={
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Typography.Text>Copy your key now. For security reasons, you won't be able to see it again.</Typography.Text>
                            <Typography.Text code strong copyable={{ text: createdKey.key }}>
                                {createdKey.key}
                            </Typography.Text>
                        </Space>
                    }
                />
            )}

            {showForm ? (
                <Form form={form} layout="vertical" onFinish={onCreate} initialValues={{ scope: ['events:read'] }}>
                    <Form.Item
                        name="scope"
                        label={<Typography.Text strong>Scope</Typography.Text>}
                        rules={[{ required: true, message: 'Please select at least one scope!' }]}
                    >
                        <Select mode="multiple" placeholder="Select scope" options={API_KEY_SCOPES} />
                    </Form.Item>
                    <Form.Item name="description" label={<Typography.Text strong>Description</Typography.Text>}>
                        <Input.TextArea placeholder="What is this key used for?" rows={2} maxLength={128} showCount />
                    </Form.Item>
                    <Space>
                        <Button type="primary" htmlType="submit" loading={createMutation.isLoading}>
                            Create
                        </Button>
                        <Button onClick={cancelForm}>Cancel</Button>
                    </Space>
                    <Divider />
                </Form>
            ) : (
                <Button type="primary" icon={<KeyRound size={14} />} style={{ marginBottom: 16 }} onClick={openForm}>
                    Create API Key
                </Button>
            )}

            <ApiDocCard />
            <br />

            <Table<IApiKey>
                rowKey="id"
                size="small"
                loading={isLoading}
                columns={columns}
                dataSource={apiKeys}
                pagination={{ hideOnSinglePage: true, pageSize: 10 }}
            />
        </Modal>
    );
};

const ApiDocCard = () => {
    return (
        <Card style={{ width: '100%' }}>
            <Meta
                avatar={<BookOpenText />}
                title="API Documentation"
                description={
                    <span>
                        Read the{' '}
                        <Button
                            color="default"
                            size="small"
                            variant="text"
                            target="_blank"
                            href="https://documentation.enersponse.com/events/integration/index.html"
                        >
                            documentation <SquareArrowOutUpRight size={14} />
                        </Button>{' '}
                        to learn how to use your API keys.
                    </span>
                }
            />
        </Card>
    );
};
